import { useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  useDrawer
} from '@cat-ecom/pcc-components';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { CatButton } from '@blocks-react/components/Button';
import AddressFormFields from '../../Blocks/AddressFormFields';
import AVIMobileDrawer from '@app/components/common/address/AVIMobileDrawer/AVIMobileDrawer';
import WrapperContainer from '@app/components/common/WrapperContainer';
import AddAddressMobileDrawer from '@app/components/common/address/AddAddressMobileDrawer/AddAddressMobileDrawer';
import { useSystemFeedback } from '@app/hooks';
import { isSummaryAndPaymentPage } from '@app/utils/browserUtils';
import { getStateByCode, getStatesInSelectedCountry } from '../utils';
import { MODALS_ROOT } from '@app/constants/targets';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import styles from './AddressMobileDrawer.module.scss';

const AddressMobileDrawer = ({
  addresses,
  addressLimit,
  countries = [],
  initialValues = {},
  isGuest = false,
  onSubmitForm,
  requiredFields = [],
  showPhoneNumberWithVerification,
  showNeighborhood,
  showAddButton = true,
  isDealerUser = false,
  profileAddressId = '',
  showAddressLine2 = false,
  setSelected,
  countryDisplayName,
  withDropdownSearch = false,
  errorSelector,
  errorPath,
  aviMethods,
  ...mobileDrawerProps
}) => {
  const [t] = useTranslation();
  const { clearError } = useSystemFeedback();
  const { state = '' } = initialValues;
  const {
    isAVIMobileOpen,
    setIsAVIMobileOpen,
    aviNotNeeded,
    setAviNotNeeded,
    onAVIContinue,
    aviAddressData
  } = aviMethods;

  const statesOrProvinces = getStatesInSelectedCountry(
    countries,
    countryDisplayName
  );

  const { displayName: stateDisplayName } = getStateByCode(
    statesOrProvinces,
    state,
    {
      displayName: state
    }
  );

  const { reset, getValues, trigger } = mobileDrawerProps.addressDrawerMethods;

  const { closeDrawer, drawerProps } = useDrawer({
    onClose: () => setIsAVIMobileOpen(false)
  });

  const {
    closeDrawer: closeAddAddressDrawer,
    drawerProps: AddAddressProps,
    openDrawer: openAddAddressDrawer
  } = useDrawer({
    onClose: () => {
      reset({
        ...initialValues,
        country: countryDisplayName,
        state: stateDisplayName,
        phoneCode: ''
      });
    }
  });

  const AddAddressDrawerCloseHandler = () => {
    closeAddAddressDrawer();
    mobileDrawerProps.closeDrawer();
  };

  const AddAddressDrawerProps = {
    closeDrawer: AddAddressDrawerCloseHandler,
    returnToEdit: closeAddAddressDrawer,
    ...AddAddressProps
  };

  const AVIMobileDrawerProps = {
    closeDrawer,
    AddAddressDrawerCloseHandler,
    setSelected,
    ...drawerProps
  };

  useEffect(() => {
    reset({
      ...initialValues,
      neighborhood: isEmpty(initialValues.neighborhood)
        ? ''
        : initialValues.neighborhood,
      address2: isEmpty(initialValues.address2) ? '' : initialValues.address2,
      country: countryDisplayName,
      state: stateDisplayName
    });
  }, [initialValues, countryDisplayName, stateDisplayName]);

  const isSelfAddressNonDealer =
    isSummaryAndPaymentPage() &&
    !isDealerUser &&
    !isEmpty(profileAddressId) &&
    profileAddressId === initialValues?.addressId;

  const showAddressName = !(
    isGuest ||
    (isSummaryAndPaymentPage() && isDealerUser)
  );

  const onSubmit = async () => {
    clearError(ERROR_DOMAIN.CHECKOUT, ERROR_PATH.MODAL);
    const fieldsHaveNoErrors = await trigger([
      'nickName',
      'firstName',
      'lastName',
      'country',
      'neighborhood',
      'zipCode',
      'address1',
      'address2',
      'city',
      'state',
      'phone1',
      'email'
    ]);
    if (fieldsHaveNoErrors) {
      onSubmitForm(getValues());
      if (AddAddressDrawerProps.isDrawerOpen) {
        AddAddressDrawerProps.closeDrawer();
      }
    }
  };
  const onAddNewAddress = () => {
    openAddAddressDrawer();
    reset({
      nickName: '',
      firstName: '',
      lastName: '',
      country: countryDisplayName,
      zipCode: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      phone1: '',
      email: '',
      neighborhood: ''
    });
  };

  useEffect(() => {
    if (aviNotNeeded) {
      setAviNotNeeded(false);
      mobileDrawerProps.closeDrawer();
    }
  }, [aviNotNeeded]);

  return (
    <WrapperContainer targetId={MODALS_ROOT}>
      <FormProvider {...mobileDrawerProps.addressDrawerMethods}>
        <form id="address-blocks-form">
          <Drawer {...mobileDrawerProps}>
            <DrawerHeader>
              <h4
                className={cx(
                  'cat-u-theme-typography-title m-0 text-capitalize',
                  styles['edit-address-mobile__title']
                )}
              >
                {t('ADDR_EDIT_ADDRESS')}
              </h4>
            </DrawerHeader>
            <DrawerBody>
              {!isGuest && showAddButton && (
                <CatButton
                  data-testid="cat-button"
                  variant="ghost"
                  size="sm"
                  className={cx(
                    styles['edit-address-mobile__add-new-address'],
                    'mb-3'
                  )}
                  onClick={onAddNewAddress}
                >
                  {t('ADD_A_NEW_ADDRESS')}
                </CatButton>
              )}
              <AddressFormFields
                isAddressNameEditable={isSelfAddressNonDealer}
                showAddressLine2={!!initialValues.address2 || showAddressLine2}
                showAddressName={showAddressName}
                showEmail
                showName
                showPhoneNumber
                countryData={countries}
                addressLimit={addressLimit}
                requiredFields={requiredFields}
                selectedCountry={countryDisplayName}
                showPhoneNumberWithVerification={
                  showPhoneNumberWithVerification
                }
                showNeighborhood={showNeighborhood}
                isDealerUser={isDealerUser}
                profileAddressId={profileAddressId}
                autoCompleteOnZipcode
                withDropdownSearch={withDropdownSearch}
              />
            </DrawerBody>
            <DrawerFooter className="d-flex gap-3">
              <CatButton variant="primary" fullwidth onClick={onSubmit}>
                {t('UPDATE')}
              </CatButton>
              <CatButton
                variant="ghost"
                fullwidth
                onClick={mobileDrawerProps.closeDrawer}
              >
                {t('CANCEL')}
              </CatButton>
            </DrawerFooter>
            <AVIMobileDrawer
              {...AVIMobileDrawerProps}
              isActive={isAVIMobileOpen}
              onSubmit={onAVIContinue}
              aviAddressData={aviAddressData}
              current={getValues()}
              errorSelector={errorSelector}
              errorPath={errorPath}
            />
            <AddAddressMobileDrawer
              {...AddAddressDrawerProps}
              showAddressLine2={showAddressLine2}
              showAddressName={showAddressName}
              countries={countries}
              addressLimit={addressLimit}
              requiredFields={requiredFields}
              selectedCountry={countryDisplayName}
              showPhoneNumberWithVerification={showPhoneNumberWithVerification}
              showNeighborhood={showNeighborhood}
              isDealerUser={isDealerUser}
              profileAddressId={profileAddressId}
              withDropdownSearch={withDropdownSearch}
              onSubmit={onSubmit}
            />
          </Drawer>
        </form>
      </FormProvider>
    </WrapperContainer>
  );
};

AddressMobileDrawer.propTypes = {
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      nickName: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      addressId: PropTypes.string,
      addressType: PropTypes.string,
      default: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    })
  ),
  addressLimit: PropTypes.string,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      displayName: PropTypes.string,
      states: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          displayName: PropTypes.string
        })
      )
    })
  ),
  initialValues: PropTypes.shape({
    nickName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    country: PropTypes.string,
    zipCode: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string
  }),
  isGuest: PropTypes.bool,
  onSubmitForm: PropTypes.func,
  setSelected: PropTypes.func,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  showPhoneNumberWithVerification: PropTypes.bool,
  showNeighborhood: PropTypes.bool,
  showAddButton: PropTypes.bool,
  showFormLoader: PropTypes.bool,
  isDealerUser: PropTypes.bool,
  profileAddressId: PropTypes.string,
  labelClassName: PropTypes.string,
  showAddressLine2: PropTypes.bool,
  countryDisplayName: PropTypes.string,
  withDropdownSearch: PropTypes.bool,
  aviMethods: PropTypes.shape({
    isAVIMobileOpen: PropTypes.bool,
    setIsAVIMobileOpen: PropTypes.func,
    aviNotNeeded: PropTypes.bool,
    setAviNotNeeded: PropTypes.func,
    onAVIContinue: PropTypes.func,
    aviAddressData: PropTypes.shape({})
  }),
  errorSelector: PropTypes.string,
  errorPath: PropTypes.string
};

export default AddressMobileDrawer;
